import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Section,
  Eyebrow,
  Headline,
  Lead,
  Card,
  Grid,
  Col,
} from '../../components/iss/IssPrimitives';
import './EvaluationCompletePage.scss';

const COMPLETED_PHASES = [
  { name: 'Initiation', detail: 'Scorecard and business justification' },
  { name: 'Preparation', detail: 'NDA, IT security, legal, procurement' },
  { name: 'Running', detail: 'Duration, templates and reminders' },
  { name: 'Evaluation', detail: 'Success criteria, report and outcome' },
];

type LocationState = {
  businessJustification?: { title?: unknown };
};

function greetingNameFrom(displayName: string): string {
  const trimmed = displayName.trim();

  if (trimmed.includes(',')) {
    const given = trimmed.split(',')[1]?.trim() ?? '';
    return given.split(/\s+/).filter(Boolean)[0] || trimmed;
  }

  if (trimmed.includes('@')) {
    const local = trimmed.split('@')[0] || trimmed;
    return local.split(/[._\s-]+/).filter(Boolean)[0] || local;
  }

  return trimmed.split(/\s+/).filter(Boolean)[0] || trimmed;
}

const EvaluationCompletePage: React.FC = () => {
  const { accounts } = useMsal();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? {};
  const displayName = accounts[0]?.name || accounts[0]?.username || '';
  const greetingName = displayName ? greetingNameFrom(displayName) : null;
  const pocTitle =
    typeof state.businessJustification?.title === 'string'
      ? state.businessJustification.title.trim()
      : '';

  return (
    <div className="eval-complete">
      <Section theme="dark" spacing="tight">
        <div className="eval-complete__hero">
          <span className="eval-complete__seal" aria-hidden>
            <span className="eval-complete__check" />
          </span>
          <div className="eval-complete__hero-copy">
            <Eyebrow>POC complete</Eyebrow>
            <Headline as="h1" size="4xl">
              {greetingName
                ? `Congratulations, ${greetingName}`
                : 'Congratulations'}
            </Headline>
            <Lead>You have completed the Proof of Concept process.</Lead>
          </div>
        </div>
      </Section>

      <Section theme="sand">
        <div className="eval-complete__body">
          <div className="eval-complete__next">
            <Eyebrow>What next?</Eyebrow>
            <Headline as="h2" size="3xl">
              Choose an action
            </Headline>
            <Lead>
              Return to the platform, start another PoC, or open My PoCs to find
              this one again.
            </Lead>

            <Grid>
              <Col span={4} md={4} lg={6}>
                <Card
                  filled
                  to="/"
                  meta="Home"
                  title="Return to home"
                  text="Go back to the Proof of Concept platform."
                />
              </Col>
              <Col span={4} md={4} lg={6}>
                <Card
                  filled
                  to="/pocs/new"
                  meta="Action"
                  title="Start a new PoC"
                  text="Begin another proof of concept with an external supplier."
                />
              </Col>
            </Grid>
          </div>

          <aside className="eval-complete__journey" aria-label="Completed PoC journey">
            <div className="eval-complete__journey-card">
              <span className="eval-complete__badge">Complete</span>
              <p className="eval-complete__journey-eyebrow">PoC journey</p>
              <h3 className="eval-complete__journey-title">
                {pocTitle || 'All four phases finished'}
              </h3>

              <ol className="eval-complete__phases">
                {COMPLETED_PHASES.map((phase, index) => (
                  <li key={phase.name} className="eval-complete__phase">
                    <span className="eval-complete__dot" aria-hidden>
                      {index + 1}
                    </span>
                    <div className="eval-complete__phase-body">
                      <strong>{phase.name}</strong>
                      <span>{phase.detail}</span>
                    </div>
                  </li>
                ))}
              </ol>

              <div className="eval-complete__journey-footer">
                <Link className="eval-complete__mypocs" to="/my-pocs">
                  Find this PoC in My PoCs →
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </Section>
    </div>
  );
};

export default EvaluationCompletePage;
